// ./src/utils/api/CategoryService.ts
//
// Category (Library → reusable categories) service. OpResult-native. System
// defaults + the account's own categories are returned; only the account's
// account-wide categories are editable (see the `rights` field). Referenced by
// points of interest today; budgeting / tasks later. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants (mirror the eventsmagic-api CATEGORY_GROUP boundary)
// =============================================================================

export const CATEGORY_GROUP_VALUES = [
  "general",
  "poi",
  "budget",
  "task",
  "appointment",
  "vendor",
] as const;
export type CategoryGroup = (typeof CATEGORY_GROUP_VALUES)[number];

/**
 * Mantine palette per category group — the tint a category falls back to when it
 * carries no colour of its own (most seeded ones don't).
 */
export const CATEGORY_GROUP_COLOR: Record<string, string> = {
  general: "gray",
  poi: "blue",
  budget: "green",
  task: "violet",
  appointment: "cyan",
  vendor: "orange",
  map: "grape",
  zone: "teal",
};

/**
 * How a category should LOOK — one implementation for the Library list and every
 * picker, so they never drift. A category with no icon still gets a Tag glyph and
 * a group tint; its own colour wins when set.
 */
export const categoryIconName = (category: Pick<Category, "icon">): string => category.icon || "Tag";

export const categoryIconColor = (category: Pick<Category, "color" | "categoryGroup">): string =>
  category.color ||
  `var(--mantine-color-${CATEGORY_GROUP_COLOR[category.categoryGroup ?? "general"] ?? "gray"}-6)`;

// =============================================================================
// GraphQL
// =============================================================================

const CATEGORY_FIELDS = `
  id
  accountId
  spaceId
  code
  name
  categoryGroup
  description
  icon
  color
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const CATEGORY_LIST_QUERY = gql`
  query CategoryList($filter: CategoryFilter, $params: PaginationAndSorting) {
    categoryList(filter: $filter, params: $params) {
      code
      data { ${CATEGORY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CATEGORY_GET_QUERY = gql`
  query CategoryGet($id: ID) {
    categoryGet(id: $id) {
      code
      data { ${CATEGORY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CATEGORY_GET_MANY_QUERY = gql`
  query CategoryGetMany($ids: [ID]) {
    categoryGetMany(ids: $ids) {
      code
      data { ${CATEGORY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CATEGORY_CREATE_MUTATION = gql`
  mutation CategoryCreate($params: CategoryInput) {
    categoryCreate(params: $params) {
      code
      data { ${CATEGORY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CATEGORY_UPDATE_MUTATION = gql`
  mutation CategoryUpdate($where: CategoryWhereInput, $params: CategoryInput) {
    categoryUpdate(where: $where, params: $params) {
      code
      data { ${CATEGORY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CATEGORY_REMOVE_MUTATION = gql`
  mutation CategoryRemove($where: CategoryWhereInput) {
    categoryRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Category schema)
// =============================================================================

export interface Category {
  id?: string;
  accountId?: string | null;
  spaceId?: string | null;
  code?: string | null;
  name?: string | null;
  categoryGroup?: string | null;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). Empty = read-only. */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CategoryInput {
  name?: string | null;
  categoryGroup?: string | null;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface CategoryBulkInput {
  status?: number | null;
}

export interface CategoryFilter {
  id?: string[] | null;
  code?: string[] | null;
  categoryGroup?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface CategoryWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class CategoryService {
  static async list(
    filter?: CategoryFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(CATEGORY_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: CategoryFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<Category>(CategoryService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(CATEGORY_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(CATEGORY_GET_MANY_QUERY, { ids });
  }

  static async create(params: CategoryInput): Promise<OpResult> {
    return ApiService.executeMutation(CATEGORY_CREATE_MUTATION, { params });
  }

  static async update(where: CategoryWhereInput, params: CategoryInput): Promise<OpResult> {
    return ApiService.executeMutation(CATEGORY_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(CATEGORY_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status on every selected category in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: CategoryBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(CATEGORY_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected category in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(CATEGORY_REMOVE_MUTATION, { where: { id: ids } });
  }
}
